'use client'

import ChevronDownIcon from '@/components/icons/chevron-down'
import ChevronUpIcon from '@/components/icons/chevron-up'
import { useCallbackRef } from '@/hooks/use-callback-ref'
import useControlled from '@/hooks/use-controlled'
import useId from '@/hooks/use-id'
import clamp from '@/utils/clamp'
import generateClassNames from '@/utils/generate-class-names'
import isNumber from '@/utils/is-number'
import clsx from 'clsx'
import mergeRefs from 'merge-refs'
import React, { forwardRef, useRef, useState } from 'react'
import {
  NumericFormat,
  NumericFormatProps,
  OnValueChange,
} from 'react-number-format'
import InputBase, { InputBaseProps } from '../input-base'
import './number-input.scss'

const classes = generateClassNames('number-input', [
  'controls',
  'increment',
  'decrement',
])

const SUPPORTED_KEYS = [
  'ArrowUp',
  'ArrowDown',
  'PageUp',
  'PageDown',
  'Home',
  'End',
]

function getDecimalSeparator(locale?: string) {
  return (1.1).toLocaleString(locale)[1]
}

function getThousandSeparator(locale?: string) {
  return (1000).toLocaleString(locale)[1]
}

const NumericFormatCustom = forwardRef(function NumericFormatCustom(
  props: Omit<NumericFormatProps, 'getInputRef'>,
  ref: React.ForwardedRef<HTMLInputElement>
) {
  return <NumericFormat getInputRef={ref} {...props} />
})

export type NumberInputProps = Omit<
  InputBaseProps<React.PropsWithoutRef<typeof NumericFormatCustom>>,
  'inputComponent' | 'onValueChange'
> & {
  defaultValue?: number
  hideControls?: boolean
  max?: number
  min?: number
  onValueChange?: (value: number | undefined) => void
  step?: number
  value?: number
}

const NumberInput = forwardRef(function NumberInput(
  props: NumberInputProps,
  ref: React.ForwardedRef<HTMLInputElement>
) {
  const {
    'aria-describedby': ariaDescribedby,
    className,
    decimalSeparator = getDecimalSeparator(),
    defaultValue,
    disabled,
    hideControls,
    id: idProp,
    max,
    min,
    name,
    onBlur,
    onFocus,
    onKeyDown,
    onValueChange,
    readOnly,
    step = 1,
    thousandSeparator = getThousandSeparator(),
    value: valueProp,
    ...other
  } = props

  const inputRef = useRef<HTMLInputElement>(null)
  const onStepTimeoutRef = useRef<number | null>(null)
  const stepHoldingRef = useRef<boolean>(false)
  const id = useId(idProp)

  /**
   * The dirty `value` of the `input` element when it is in focus.
   */
  const [inputValue, setInputValue] = useState(valueProp ?? defaultValue)

  /**
   * The clamped `value` of the `input` element.
   */
  const [value, setValue] = useControlled({
    value: valueProp,
    defaultValue,
  })

  const [focused, setFocused] = useState(false)

  const isIncrementDisabled =
    disabled ||
    (isNumber(value) ? value >= (max ?? Number.MAX_SAFE_INTEGER) : false)

  const isDecrementDisabled =
    disabled ||
    (isNumber(value) ? value <= (min ?? Number.MIN_SAFE_INTEGER) : false)

  const increment = useCallbackRef(() => {
    const newValue = isNumber(value) ? value + step : min ?? 1
    const clampedValue = clamp(newValue, min, max)

    setInputValue(clampedValue)
    setValue(clampedValue)
    onValueChange?.(clampedValue)
  })

  const decrement = useCallbackRef(() => {
    const newValue = isNumber(value) ? value - step : max ?? -1
    const clampedValue = clamp(newValue, min, max)

    setInputValue(clampedValue)
    setValue(clampedValue)
    onValueChange?.(clampedValue)
  })

  const incrementToMax = useCallbackRef(() => {
    if (max) {
      setValue(max)
      onValueChange?.(max)
    }
  })

  const decrementToMin = useCallbackRef(() => {
    if (min) {
      setValue(min)
      onValueChange?.(min)
    }
  })

  const handleValueChange: OnValueChange = (values, sourceInfo) => {
    const { floatValue } = values

    setInputValue(floatValue)

    if (sourceInfo.source === 'event') {
      const clampedValue = isNumber(floatValue)
        ? clamp(floatValue, min, max)
        : undefined

      setValue(clampedValue)
      onValueChange?.(clampedValue)
    }
  }

  const handleStep = (direction: 'up' | 'down') => {
    if (direction === 'up') {
      increment()
    } else {
      decrement()
    }

    onStepTimeoutRef.current = window.setTimeout(
      () => handleStep(direction),
      stepHoldingRef.current ? 50 : 500
    )
    stepHoldingRef.current = true
  }

  const handleStepDone = () => {
    if (onStepTimeoutRef.current) {
      window.clearTimeout(onStepTimeoutRef.current)
      onStepTimeoutRef.current = null
      stepHoldingRef.current = false
    }
  }

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    onKeyDown?.(event)

    if (event.defaultPrevented || readOnly) {
      return
    }

    if (SUPPORTED_KEYS.includes(event.key)) {
      event.preventDefault()
    }

    switch (event.key) {
      case 'ArrowUp':
        increment()
        break
      case 'ArrowDown':
        decrement()
        break
      case 'PageUp':
        increment()
        break
      case 'PageDown':
        decrement()
        break
      case 'Home':
        incrementToMax()
        break
      case 'End':
        decrementToMin()
        break
    }
  }

  const handleFocus = (event: React.FocusEvent<HTMLInputElement>) => {
    if (onFocus) {
      onFocus(event)

      if (event.defaultPrevented) {
        return
      }
    }

    setFocused(true)
  }

  const handleBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    if (onBlur) {
      onBlur(event)

      if (event.defaultPrevented) {
        return
      }
    }

    setInputValue(value)
    setFocused(false)
  }

  const controls =
    !hideControls && !readOnly ? (
      <div className={classes.controls}>
        <button
          aria-controls={id}
          className={classes.increment}
          disabled={isIncrementDisabled}
          onMouseDown={(event) => {
            event.preventDefault()
            handleStep('up')
          }}
          onMouseLeave={handleStepDone}
          onMouseUp={handleStepDone}
          tabIndex={-1}
        >
          <ChevronUpIcon size='sm' />
        </button>
        <button
          aria-controls={id}
          className={classes.decrement}
          disabled={isDecrementDisabled}
          onMouseDown={(event) => {
            event.preventDefault()
            handleStep('down')
          }}
          onMouseLeave={handleStepDone}
          onMouseUp={handleStepDone}
          tabIndex={-1}
        >
          <ChevronDownIcon size='sm' />
        </button>
      </div>
    ) : null

  return (
    <InputBase
      allowNegative={isNumber(min) && min < 0}
      className={clsx(classes.root, className)}
      controls={controls}
      decimalSeparator={decimalSeparator}
      disabled={disabled}
      fixedDecimalScale
      id={id}
      inputComponent={NumericFormatCustom}
      max={max}
      min={min}
      onBlur={handleBlur}
      onFocus={handleFocus}
      onKeyDown={handleKeyDown}
      onValueChange={handleValueChange}
      readOnly={readOnly}
      ref={mergeRefs(inputRef, ref)}
      thousandSeparator={thousandSeparator}
      value={focused && !readOnly ? inputValue : value}
      {...other}
    />
  )
})

export default NumberInput
